<script>
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';

const DYMO_VENDOR_ID = 0x0922; // USB vendor ID for DYMO, used to filter the device picker
const SCALE_REPORT_ID = 3; // HID input report ID the S100 sends weight data on
const SCALE_UNIT_LB = 12; // HID unit code confirmed as pounds on this scale
const SCALE_STATUS_IN_MOTION = 3; // HID status byte value while the reading hasn't settled yet

// Module-level singleton: every component calling useScaleReader() (the
// header status control and the weight-entry capture control) shares this
// same state and the same device, so only one HID connection is ever
// attempted no matter how many of them are mounted at once.
const connectionState = ref('disconnected'); // 'disconnected' | 'connecting' | 'connected'
const liveWeight = ref(null);
const statusCode = ref(null);
const errorType = ref(null); // 'open_failed' | null
const lastAttemptFailed = ref(false);

let device = null;
let manuallyDisconnected = false;
let reconnectInFlight = false;
let activeConsumers = 0;
let listenersAttached = false;

function clearError() {
    errorType.value = null;
}

function resetReading() {
    liveWeight.value = null;
    statusCode.value = null;
}

function handleInputReport(event) {
    if (event.reportId !== SCALE_REPORT_ID || event.data.byteLength < 5) {
        return;
    }

    const status = event.data.getUint8(0);
    const unit = event.data.getUint8(1);
    let exponent = event.data.getUint8(2);
    if (exponent > 127) {
        exponent -= 256;
    }
    const weightRaw = event.data.getUint16(3, true);

    if (unit !== SCALE_UNIT_LB) {
        console.warn(`Unexpected DYMO scale unit code ${unit} (expected ${SCALE_UNIT_LB} = lb)`);
        return;
    }

    statusCode.value = status;
    liveWeight.value = weightRaw * (10 ** exponent);
}

async function openDevice(candidate) {
    device = candidate;
    device.addEventListener('inputreport', handleInputReport);
    await device.open();
    connectionState.value = 'connected';
    lastAttemptFailed.value = false;
}

async function attemptSilentReconnect() {
    if (manuallyDisconnected || connectionState.value !== 'disconnected' || reconnectInFlight) {
        return;
    }
    reconnectInFlight = true;
    try {
        const granted = await navigator.hid.getDevices();
        const candidate = granted.find(d => d.vendorId === DYMO_VENDOR_ID);
        if (!candidate) {
            return;
        }
        connectionState.value = 'connecting';
        await openDevice(candidate);
    } catch (error) {
        device = null;
        connectionState.value = 'disconnected';
        lastAttemptFailed.value = true;
    } finally {
        reconnectInFlight = false;
    }
}

function handleHidDisconnect(event) {
    if (event.device !== device) {
        return;
    }
    device = null;
    connectionState.value = 'disconnected';
    resetReading();
}

function handleHidConnect(event) {
    if (event.device.vendorId !== DYMO_VENDOR_ID) {
        return;
    }
    attemptSilentReconnect();
}

async function connect() {
    if (connectionState.value !== 'disconnected') {
        return;
    }
    manuallyDisconnected = false;
    connectionState.value = 'connecting';
    try {
        const [candidate] = await navigator.hid.requestDevice({
            filters: [{ vendorId: DYMO_VENDOR_ID }],
        });
        if (!candidate) {
            connectionState.value = 'disconnected';
            return;
        }
        await openDevice(candidate);
    } catch (error) {
        connectionState.value = 'disconnected';
        lastAttemptFailed.value = true;
        errorType.value = 'open_failed';
    }
}

async function disconnect() {
    manuallyDisconnected = true;
    if (device) {
        device.removeEventListener('inputreport', handleInputReport);
        await device.close();
        device = null;
    }
    connectionState.value = 'disconnected';
    lastAttemptFailed.value = false;
    resetReading();
}

export function useScaleReader() {
    const isSupported = computed(() => 'hid' in navigator);

    onMounted(() => {
        activeConsumers += 1;
        if (!isSupported.value) {
            return;
        }
        if (!listenersAttached) {
            navigator.hid.addEventListener('disconnect', handleHidDisconnect);
            navigator.hid.addEventListener('connect', handleHidConnect);
            listenersAttached = true;
        }
        attemptSilentReconnect();
    });

    onUnmounted(() => {
        activeConsumers -= 1;
        if (activeConsumers > 0 || !isSupported.value) {
            return;
        }
        navigator.hid.removeEventListener('disconnect', handleHidDisconnect);
        navigator.hid.removeEventListener('connect', handleHidConnect);
        listenersAttached = false;
        if (device) {
            device.removeEventListener('inputreport', handleInputReport);
            device.close();
            device = null;
        }
        connectionState.value = 'disconnected';
        resetReading();
    });

    return {
        isSupported,
        connectionState,
        liveWeight,
        statusCode,
        errorType,
        lastAttemptFailed,
        clearError,
        connect,
        disconnect,
    };
}

// Silently mirrors every reading the scale reports into the given ref
// (typically a form's weight ref) - no visible UI, no "use this reading"
// step. Every inputreport updates liveWeight (see handleInputReport above),
// so this tracks the scale's own display 1:1, including the small
// in-between values while a weight is still being placed - not just the
// final reading once the scale settles. Also exposes isSettling so a
// consumer can block committing the value (e.g. an "Add" button) while a
// reading is still in motion, without affecting the live display above.
export function useScaleAutoFill(weightRef) {
    const { liveWeight, statusCode, connectionState } = useScaleReader();

    watch(liveWeight, (value) => {
        if (value !== null) {
            weightRef.value = value.toFixed(1);
        }
    });

    const isSettling = computed(() =>
        connectionState.value === 'connected' && statusCode.value === SCALE_STATUS_IN_MOTION
    );

    return { isSettling };
}
</script>
